/**
 * <norse-subscription-buy-box>
 *
 * Subscription plan selector rendered as full-width option rows.
 * Each row maps to one variant + one selling plan configured in block settings.
 * On selection: syncs product-form id input, selling_plan hidden input, and ATC button text.
 *
 * Uses the same canonical selling-plan resolution strategy as norse-buy-box.js:
 * slot-index match within selling_plan_allocations for cross-variant bundle safety.
 */

if (!customElements.get('norse-subscription-buy-box')) {

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Exact cents in the market's punctuation — see NorseSellingPlanUtils.formatMoney.
   * This used to round to whole units and fall back to `Intl` with the en-US locale, which
   * printed a price the cart never charges in any converted market ("€42" for €41,95) and
   * in the wrong punctuation ("SEK 774" beside Liquid's "774 kr").
   */
  function nsbbFormatMoney(cents) {
    const shared = window.NorseSellingPlanUtils?.formatMoney;
    if (shared) return shared(cents);
    return (Math.round(Number(cents) || 0) / 100).toFixed(2).replace(/\.00$/, '');
  }

  /**
   * Resolve the canonical selling_plan_id for a variant.
   * The UI uses IDs from selling_plan_groups[0]. Bundle variants may have
   * different IDs mapped at the same slot index.
   */
  function nsbbResolveCanonicalPlanId(product, variant, uiPlanId) {
    if (!uiPlanId) return '';
    const uiPlanIdN = Number(uiPlanId);
    if (!Number.isFinite(uiPlanIdN)) return String(uiPlanId);

    const allocs = variant.selling_plan_allocations || [];

    // 1. Direct match
    const direct = allocs.find(
      a => Number(a.selling_plan_id) === uiPlanIdN || Number(a.selling_plan?.id) === uiPlanIdN
    );
    if (direct) {
      const sid = direct.selling_plan_id ?? direct.selling_plan?.id;
      return sid != null ? String(sid) : String(uiPlanIdN);
    }

    // 2. Slot-index fallback
    const primaryPlans = product.selling_plan_groups?.[0]?.selling_plans || [];
    const slotIdx = primaryPlans.findIndex(p => Number(p.id) === uiPlanIdN);
    if (slotIdx >= 0 && slotIdx < allocs.length) {
      const mapped = allocs[slotIdx];
      const sid = mapped?.selling_plan_id ?? mapped?.selling_plan?.id;
      return sid != null ? String(sid) : String(uiPlanIdN);
    }

    return String(uiPlanIdN);
  }

  // ---------------------------------------------------------------------------
  // Custom element
  // ---------------------------------------------------------------------------

  customElements.define(
    'norse-subscription-buy-box',
    class NorseSubscriptionBuyBox extends HTMLElement {

      connectedCallback() {
        this._fetchRunId = 0;
        this._onGenderChange = this._onGenderChange.bind(this);
        this._bindOptions();
        this._bindGiftPopups();
        this._bindGiftBundling();
        this._bindGenderSwitch();
        this._syncFormFromSelected();
        this._updateButtonText();
        this._syncGiftBlock();
        void this._resolveAndSyncPlanId();
      }

      disconnectedCallback() {
        if (this._onGenderChange) {
          document.removeEventListener('norse:gender-change', this._onGenderChange);
        }
      }

      // ── section ID accessor (mirrors norse-buy-box pattern) ──────────────

      get sectionId() {
        const declared = this.dataset.sectionId || '';
        if (declared && document.getElementById(`product-form-${declared}`)) return declared;
        const infoSection = this.closest('[id^="ProductInfo-"]');
        if (infoSection) {
          const id = infoSection.id.replace('ProductInfo-', '');
          if (id && document.getElementById(`product-form-${id}`)) return id;
        }
        return declared;
      }

      // ── gender (unisex landing page) ──────────────────────────────────────
      //
      // Opt-in: only the rows rendered with a MALE counterpart carry the `*-male`
      // attributes, so a buy box without gender switching resolves to the plain ones.
      // Prices are identical across genders, so nothing visible has to be re-rendered —
      // only the ids the form posts.

      get isMale() {
        return document.documentElement.dataset.norseGender === 'male';
      }

      get productHandle() {
        if (this.isMale && this.dataset.productHandleMale) return this.dataset.productHandleMale;
        return this.dataset.productHandle || '';
      }

      _variantIdFor(option) {
        return (this.isMale && option.dataset.variantIdMale) || option.dataset.variantId || '';
      }

      _sellingPlanIdFor(option) {
        if (this.isMale && option.dataset.variantIdMale) {
          return option.dataset.sellingPlanIdMale || '';
        }
        return option.dataset.sellingPlanId || '';
      }

      _bindGenderSwitch() {
        if (!this.dataset.productHandleMale) return;
        document.addEventListener('norse:gender-change', this._onGenderChange);
      }

      _onGenderChange() {
        this._syncFormFromSelected();
        void this._resolveAndSyncPlanId();
      }

      // ── event binding ────────────────────────────────────────────────────

      _bindOptions() {
        this.querySelectorAll('[data-nsbb-option]').forEach(opt => {
          opt.addEventListener('click', e => {
            // Don't select when clicking inside the gift block (e.g. "Learn more")
            if (e.target.closest('[data-nsbb-gift]')) return;
            this._selectOption(opt);
          });
          opt.addEventListener('keydown', e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this._selectOption(opt);
            }
          });
        });
      }

      // ── gift popups ──────────────────────────────────────────────────────

      _bindGiftPopups() {
        // Open popup on "Learn more" click
        this.addEventListener('click', e => {
          const btn = e.target.closest('[data-open-gift-popup]');
          if (!btn) return;
          e.stopPropagation();
          const uid = btn.dataset.openGiftPopup;
          const overlay = document.getElementById(`nsbb-popup-${uid}`);
          if (overlay) this._openPopup(overlay);
        });

        // Close on close-button click (delegated to document so it works even if popup is outside `this`)
        document.addEventListener('click', e => {
          if (e.target.closest('[data-close-gift-popup]')) {
            const overlay = e.target.closest('.nsbb__popup-overlay');
            if (overlay) this._closePopup(overlay);
          }
          // Close on backdrop click
          if (e.target.matches('.nsbb__popup-overlay')) {
            this._closePopup(e.target);
          }
        });

        // ESC key
        document.addEventListener('keydown', e => {
          if (e.key !== 'Escape') return;
          document.querySelectorAll('.nsbb__popup-overlay:not([hidden])').forEach(o => this._closePopup(o));
        });
      }

      _openPopup(overlay) {
        overlay.removeAttribute('hidden');
        overlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('nsbb-body-lock');
      }

      _closePopup(overlay) {
        if (!overlay) return;
        overlay.setAttribute('hidden', '');
        overlay.setAttribute('aria-hidden', 'true');
        // Only remove lock if no other popups are open
        if (!document.querySelector('.nsbb__popup-overlay:not([hidden])')) {
          document.body.classList.remove('nsbb-body-lock');
        }
      }

      // ── gift bundling (add-to-cart with _bundle_id) ──────────────────────

      _bindGiftBundling() {
        const form = document.getElementById(`product-form-${this.sectionId}`);
        if (!form) return;

        // Intercept in capture phase so we run before product-form's submit handler
        form.addEventListener('submit', async e => {
          const sel = this.querySelector('[data-nsbb-option].is-selected');
          const raw = sel?.dataset.giftVariantIds || '';
          const giftIds = raw.split(',').map(s => s.trim()).filter(Boolean);

          // No gifts — let the native product-form handle this normally
          if (!giftIds.length) return;

          e.preventDefault();
          e.stopImmediatePropagation();

          const btn = document.querySelector(`#ProductSubmitButton-${this.sectionId}`);
          const loader = btn?.querySelector('.loading__spinner, .loading-overlay__spinner');
          if (btn) btn.setAttribute('aria-disabled', 'true');
          if (loader) loader.classList.remove('hidden');

          const bundleId = `nsbb_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`;
          const mainVariantId = form.querySelector('input[name="id"]')?.value;
          const sellingPlanVal = this.querySelector('[data-nsbb-selling-plan-input]')?.value;

          // Collect sections to render so the cart UI updates in one shot
          const cartUi = (
            document.querySelector('cart-notification') ||
            document.querySelector('cart-drawer')
          );
          let sectionsToRender = [];
          if (cartUi?.getSectionsToRender) {
            try { sectionsToRender = cartUi.getSectionsToRender().map(s => s.id); } catch {}
          }

          const items = [
            {
              id: mainVariantId,
              quantity: 1,
              ...(sellingPlanVal ? { selling_plan: sellingPlanVal } : {}),
              properties: { _bundle_id: bundleId }
            },
            ...giftIds.map(id => ({
              id,
              quantity: 1,
              properties: { _bundle_id: bundleId, _is_gift: 'true' }
            }))
          ];

          const payload = { items };
          if (sectionsToRender.length) {
            payload.sections = sectionsToRender;
            payload.sections_url = window.location.pathname;
          }

          const root = window.Shopify?.routes?.root || '/';

          try {
            const res = await fetch(`${root}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(payload)
            });
            const data = await res.json();

            if (!res.ok) {
              const errWrapper = form.querySelector('.product-form__error-message-wrapper');
              const errText    = form.querySelector('.product-form__error-message');
              if (errWrapper) errWrapper.removeAttribute('hidden');
              if (errText)    errText.textContent = data.description || data.message || 'Unable to add to cart.';
              return;
            }

            // Hide any previous errors
            const errWrapper = form.querySelector('.product-form__error-message-wrapper');
            if (errWrapper) errWrapper.setAttribute('hidden', '');

            // Update cart UI using the sections returned by the API
            if (data.sections && cartUi) {
              if (typeof cartUi.renderContents === 'function') {
                cartUi.renderContents(data);
              }
              if (typeof cartUi.open === 'function') {
                cartUi.open();
              }
            } else if (cartUi) {
              // Fallback: ask cart-notification / cart-drawer to refresh
              document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            }

            // Standard event for other listeners (e.g. analytics)
            document.dispatchEvent(new CustomEvent('cart:add', {
              detail: { items: data.items || [] },
              bubbles: true
            }));

          } catch (err) {
            console.error('[nsbb gift bundling]', err);
          } finally {
            if (btn) btn.removeAttribute('aria-disabled');
            if (loader) loader.classList.add('hidden');
          }
        }, true /* capture — runs before product-form */);
      }

      // ── selection ────────────────────────────────────────────────────────

      _selectOption(opt) {
        // Update aria state
        this.querySelectorAll('[data-nsbb-option]').forEach(o => {
          o.classList.remove('is-selected');
          o.setAttribute('aria-pressed', 'false');
        });
        opt.classList.add('is-selected');
        opt.setAttribute('aria-pressed', 'true');

        this._syncFormFromSelected();
        this._updateButtonText();
        this._syncGiftBlock();
        this.dispatchEvent(new CustomEvent('nsbb:plan-change', { bubbles: true }));
        void this._resolveAndSyncPlanId();
      }

      // ── gift block switcher ──────────────────────────────────────────────

      _syncGiftBlock() {
        const sel = this.querySelector('[data-nsbb-option].is-selected');
        const activePlanUid = sel?.dataset.planUid || '';

        this.querySelectorAll('[data-nsbb-gift]').forEach(gift => {
          const forUid = gift.dataset.giftFor || '';
          gift.classList.toggle('is-visible', forUid === activePlanUid);
        });
      }

      // ── form sync ────────────────────────────────────────────────────────

      _syncFormFromSelected() {
        const sel = this.querySelector('[data-nsbb-option].is-selected');
        if (!sel) return;

        // Variant id
        const variantId = this._variantIdFor(sel);
        if (variantId) this._setFormVariantId(variantId);

        // Selling plan
        this._writeSellingPlanInput(this._sellingPlanIdFor(sel));
      }

      _setFormVariantId(variantId) {
        const form = document.getElementById(`product-form-${this.sectionId}`);
        const idInput = form?.querySelector('input[name="id"]');
        if (!idInput) return;
        if (String(idInput.value) === String(variantId)) return;
        idInput.value = variantId;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
        document.dispatchEvent(
          new CustomEvent('variant:change', { detail: { variantId, source: 'norse-subscription-buy-box' } })
        );
      }

      _writeSellingPlanInput(planId) {
        const hidden = this.querySelector('[data-nsbb-selling-plan-input]');
        if (!hidden) return;
        const val = planId ? String(planId) : '';
        hidden.value = val;
        const realFormId = `product-form-${this.sectionId}`;
        if (hidden.getAttribute('form') !== realFormId) {
          hidden.setAttribute('form', realFormId);
        }
        hidden.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // ── button text ──────────────────────────────────────────────────────

      _updateButtonText() {
        const sel = this.querySelector('[data-nsbb-option].is-selected');
        if (!sel) return;
        const lineCents = parseInt(sel.dataset.subLine || '0', 10);
        if (!lineCents) return;
        const btn = document.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        const span = btn?.querySelector('span');
        if (span) span.textContent = `ADD TO CART - ${nsbbFormatMoney(lineCents)}`;
      }

      // ── canonical plan ID (async, race-safe) ─────────────────────────────

      async _resolveAndSyncPlanId() {
        const sel = this.querySelector('[data-nsbb-option].is-selected');
        if (!sel) return;

        const uiPlanId  = this._sellingPlanIdFor(sel);
        const variantId = this._variantIdFor(sel);
        if (!uiPlanId || !variantId || !this.productHandle) return;

        // Write immediately so the form is never blank
        this._writeSellingPlanInput(uiPlanId);

        const runId = ++this._fetchRunId;
        try {
          const res     = await fetch(`${window.Shopify?.routes?.root || '/'}products/${this.productHandle}.js`);
          const product = await res.json();
          if (runId !== this._fetchRunId) return; // stale

          const variant = product.variants.find(v => String(v.id) === String(variantId));
          if (!variant) return;

          const canonical = nsbbResolveCanonicalPlanId(product, variant, uiPlanId);
          // Only write if this option is still selected
          if (sel.classList.contains('is-selected')) {
            this._writeSellingPlanInput(canonical);
          }
        } catch {
          // Keep UI plan ID already written
        }
      }
    }
  );
}
